<template>
	<section
		v-lazy="componentImgUrl"
		:class="[
			'pt-400-smo marketing-section bg-image-container',
			theme === 'light' ? 'bg-color-greige' : 'bg-color-content-primary'
		]"
	>
		<div class="container display-flex flex-wrap-wrap align-items-center">
			<article
				:class="[
					'col-12-smo col-8-md',
					{
						'text-color-white': theme === 'dark'
					}
				]"
			>
				<h2 class="heading-1">{{ heading }}</h2>
				<ul
					v-if="appStore.isDesktop && showBenefitsList"
					class="display-inline-flex gap-250 mt-150 ul body-xl pl-0 benefits-ul"
				>
					<li v-for="(benefit, index) in benefitsList" :key="index" class="benefits-li">
						<span>{{ benefit }}</span>
					</li>
				</ul>
				<ul v-else-if="showBenefitsList" class="checkmark-ul mt-150">
					<li
						v-for="(benefit, index) in benefitsList"
						:key="index"
						class="checkmark-li body-lg-smo mb-100 line-height-100"
					>
						<BaseSvgIcon
							class="checkmark-icon-thin text-color-content-primary mr-50 text-color-current-color"
							name="checkmark"
							height="24"
							width="24"
						/>{{ benefit }}
					</li>
				</ul>
				<slot name="cta">
					<div v-if="isWebview" class="mt-200">
						<BaseCtaLink classes="body-lg" icon="arrow" :link="learnMoreLink" />
					</div>
					<div v-else>
						<BaseLink
							:link="getStartedLink"
							classes="width-100-smo button button-primary mt-250 mt-200-smo "
						/>
					</div>
				</slot>
			</article>
		</div>
	</section>
</template>

<script setup lang="ts">
import { appCloudfrontPath } from '@constants';
import { computed } from 'vue';
import { Link } from 'types/layout';
import { useAppStore } from '@stores/app';

interface Props {
	benefitsList?: Array<string>;
	heading?: string;
	isWebview?: boolean;
	theme?: 'light' | 'dark';
}

const props = withDefaults(defineProps<Props>(), {
	benefitsList: () => ['Low fees', 'Flexible minimums', 'Quarterly liquidity'],
	heading: 'Start building a better portfolio',
	isWebview: false,
	theme: 'light'
});

const appStore = useAppStore();

const learnMoreLink: Link = { router: 'home', text: 'Learn more' };
const getStartedLink: Link = {
	text: 'Start investing',
	router: 'get-started'
};

const componentImgUrl = computed(() => {
	let path = '';
	if (appStore.isMobile) {
		path = '/images/marketing-site/make-your-move-mobile.webp';
	} else {
		path = '/images/marketing-site/make-your-move.webp';
	}

	return appCloudfrontPath + path;
});

const showBenefitsList = computed(() => {
	return props.benefitsList.length > 0;
});
</script>

<style lang="scss" scoped>
@use '../../styles/constants/colors.scss' as *;
@use '../../styles/utilities/respond-to.scss' as *;
@use 'sass:map';

.bg-image-container {
	background-repeat: no-repeat;
	@include respond-to(lg) {
		background-position: right;
		background-size: contain;
	}
	@include respond-to(mdo) {
		background-position: right -240px center;
		background-size: cover;
	}
	@include respond-to(smo) {
		min-height: 640px;
		background-size: cover;
	}
}

.checkmark-icon-thin path[pid='0'] {
	stroke-width: 1.5;
}

.checkmark-ul {
	.checkmark-li {
		font-size: 22px;
		:last-of-type {
			margin-bottom: 0;
		}
	}
}

.benefits-li::marker {
	color: map.get($brand-colors, 'rust');
}

.benefits-ul {
	.benefits-li:first-child {
		list-style-type: none;
	}
}
</style>
